import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, ScrollText, FileSignature, Landmark, Eye } from "lucide-react";
import { useSettings, SectionHeader, SaveBar, Toggle } from "./shared";
import ContractPreview from "./ContractPreview";

function AmountInput({ value, onChange, suffix, placeholder, testid }) {
  return (
    <div className="relative w-36">
      <input
        type="number"
        min="0"
        step="0.01"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="w-full h-9 px-3 pr-12 rounded-lg border border-slate-200 bg-white text-sm text-right font-medium focus:outline-none focus:border-violet-500"
        data-testid={testid}
      />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-semibold pointer-events-none">{suffix}</span>
    </div>
  );
}

function DocEditor({ icon: Icon, title, hint, value, onChange, docType, testid }) {
  const [generating, setGenerating] = useState(false);
  const generate = async () => {
    if (value && value.trim() && !window.confirm("Le texte actuel sera remplacé par la proposition de l'IA. Continuer ?")) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/vendor/legal/generate", { doc_type: docType });
      onChange(data.text);
      toast.success("Document rédigé par l'IA — relisez et ajustez avant utilisation.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur de génération");
    }
    setGenerating(false);
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={`${testid}-block`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500" />
          <div className="text-sm font-semibold text-slate-900">{title}</div>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={generating}
          className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold hover:opacity-90 disabled:opacity-50 transition-opacity"
          data-testid={`${testid}-ai-btn`}
        >
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {generating ? "Rédaction en cours…" : "Rédiger avec l'IA"}
        </button>
      </div>
      <div className="text-xs text-slate-500 mb-3">{hint}</div>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={12}
        placeholder="Rédigez votre document ici, ou laissez l'IA préparer une première version à partir de vos informations d'entreprise et de vos conditions."
        className="w-full px-3.5 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-[13px] leading-relaxed font-mono focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 focus:bg-white resize-y transition-colors"
        data-testid={`${testid}-textarea`}
      />
      <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
        <span>{(value || "").length.toLocaleString("fr-FR")} caractères</span>
        <span>Relecture par un professionnel du droit recommandée.</span>
      </div>
    </div>
  );
}

export default function LegalSection() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  useEffect(() => { if (settings) setLocal(settings.legal); }, [settings]);
  if (loading || !local) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  const dirty = JSON.stringify(local) !== JSON.stringify(settings.legal);
  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/vendor/settings", { legal: local }); toast.success("Textes juridiques enregistrés"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  return (
    <div className="max-w-4xl pb-24">
      <SectionHeader
        overline="Juridique"
        title="Textes juridiques & contrat"
        subtitle="CGV/CGL, mentions légales et modèle de contrat de location. L'IA peut rédiger une première version à partir de vos informations d'entreprise et de vos conditions."
      />

      {/* Personnalisation du contrat */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="legal-contract-options">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Personnalisation du contrat de location</div>
          <button
            type="button"
            onClick={() => setPreview(true)}
            className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full bg-slate-900 text-white text-xs font-bold hover:bg-slate-700 transition-colors shrink-0"
            data-testid="legal-preview-btn"
          >
            <Eye className="h-3.5 w-3.5" /> Aperçu du contrat
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px]">
              <Toggle label="Afficher le montant de la franchise" hint="La franchise (reste à charge en cas de sinistre) figure sur le contrat." checked={!!local.show_franchise} onChange={(v) => set("show_franchise", v)} testid="legal-toggle-franchise" />
            </div>
            {local.show_franchise && <AmountInput value={local.franchise_amount} onChange={(v) => set("franchise_amount", v)} suffix="€" placeholder="Franchise" testid="legal-franchise-amount" />}
          </div>
          <Toggle label="Afficher le montant de la caution" hint="Le montant de la caution (Réservations & Flotte → Règles) figure sur le contrat." checked={!!local.show_deposit} onChange={(v) => set("show_deposit", v)} testid="legal-toggle-deposit" />
          <Toggle label="Ajouter les CGV/CGL au contrat" hint="Vos conditions générales sont annexées à chaque contrat de location." checked={!!local.attach_cgv} onChange={(v) => set("attach_cgv", v)} testid="legal-toggle-cgv" />
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px]">
              <Toggle label="Proposer le rachat de franchise" hint="Le client peut réduire sa franchise à 0 € moyennant un supplément par jour — proposé sur votre page de réservation." checked={!!local.franchise_buyback_enabled} onChange={(v) => set("franchise_buyback_enabled", v)} testid="legal-toggle-buyback" />
            </div>
            {local.franchise_buyback_enabled && <AmountInput value={local.franchise_buyback_price_per_day} onChange={(v) => set("franchise_buyback_price_per_day", v)} suffix="€/j" placeholder="Prix" testid="legal-buyback-price" />}
          </div>
          <Toggle label="Afficher la page états des lieux" hint="La page d'état des lieux (départ/retour) est incluse dans le contrat." checked={!!local.show_inspection_page} onChange={(v) => set("show_inspection_page", v)} testid="legal-toggle-inspection" />
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px]">
              <Toggle label="Afficher le coût du kilométrage supplémentaire" hint="Kilométrage inclus et prix du km au-delà, affichés sur le contrat." checked={!!local.show_extra_km} onChange={(v) => set("show_extra_km", v)} testid="legal-toggle-extrakm" />
            </div>
            {local.show_extra_km && (
              <div className="flex items-center gap-2">
                <AmountInput value={local.included_km_per_day} onChange={(v) => set("included_km_per_day", v)} suffix="km/j" placeholder="Inclus" testid="legal-included-km" />
                <AmountInput value={local.extra_km_price} onChange={(v) => set("extra_km_price", v)} suffix="€/km" placeholder="Prix" testid="legal-extra-km-price" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Documents avec IA */}
      <div className="mt-5 space-y-4">
        <DocEditor
          icon={ScrollText}
          title="Conditions générales de location (CGV/CGL)"
          hint="Vos conditions de vente et de location : conducteur, caution, carburant, annulation, sinistres…"
          value={local.cgv}
          onChange={(v) => set("cgv", v)}
          docType="cgv"
          testid="legal-cgv"
        />
        <DocEditor
          icon={Landmark}
          title="Mentions légales"
          hint="Éditeur, forme juridique, n° d'enregistrement, RGPD… reprises depuis votre fiche Identité."
          value={local.mentions}
          onChange={(v) => set("mentions", v)}
          docType="mentions"
          testid="legal-mentions"
        />
        <DocEditor
          icon={Landmark}
          title="Politique de confidentialité"
          hint="RGPD : données collectées à la réservation, finalités, durées de conservation, droits des clients."
          value={local.privacy}
          onChange={(v) => set("privacy", v)}
          docType="privacy"
          testid="legal-privacy"
        />
        <DocEditor
          icon={FileSignature}
          title="Contrat de location (modèle)"
          hint="Modèle avec champs [À COMPLÉTER] repris pour chaque location : parties, véhicule, durée, prix, caution…"
          value={local.contract_text}
          onChange={(v) => set("contract_text", v)}
          docType="contract"
          testid="legal-contract"
        />
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="legal-save" />
      {preview && (
        <ContractPreview
          legal={local}
          company={settings.company || {}}
          deposit={settings.bookings?.deposit_amount}
          onClose={() => setPreview(false)}
        />
      )}
    </div>
  );
}
